import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { useAuth } from '../AuthContext';
import { useTheme } from '../../ThemeContext';
import BottomNav from '../BottomNav';
import Codee from '../ui/Codee';
import { useNavigate } from 'react-router-dom';

const SubscriptionForm = () => {
  const { userInfo, isLoading, isAuthenticated, payWithBonus, payWithRegular } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [balance, setBalance] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const subscriptionPlans = [
    { days: 7, cost: 100 },
    { days: 30, cost: 300 },
    { days: 90, cost: 750 },
    { days: 180, cost: 1350 },
  ];
  const PRICE_PER_DAY_BONUS = 5;
  const bonusDayOptions = [1, 5, 10, 15, 30];

  useEffect(() => {
    const initializeAuth = async () => {
      if (isAuthenticated && !isLoading) {
        setBalance(userInfo.balance);
        if (userInfo.email) {
          setEmail(userInfo.email);
        }
      }
    };
    initializeAuth();
  }, [userInfo, isLoading, isAuthenticated]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (!emailPattern.test(newEmail)) {
      setEmailError('Неверный формат email');
    } else {
      setEmailError('');
    }
  };

  const handleBonusPayment = async (days) => {
    try {
      setLoading(true);
      setError('');

      const data = await payWithBonus(days); // <-- передаём дни

      if (data?.status) {
        // Обновим баланс и сообщим срок
        if (typeof data.balance === 'number') setBalance(data.balance);
        if (data.until) alert(`Подписка продлена до ${data.until} (МСК).`);
        navigate('/');
      } else {
        // сервер вернёт detail и текущий баланс
        const msg = data?.detail || 'Не хватает денег на балансе';
        const b = typeof data?.balance === 'number' ? ` (баланс: ${data.balance}₽)` : '';
        setError(`${msg}${b}`);
      }
    } catch (err) {
      setError(err.message || 'Ошибка при оплате бонусами');
    } finally {
      setLoading(false);
    }
  };

  const handleRegularPayment = async () => {
    try {
      setError('');
      setLoading(true);

      // Проверяем валидность email и наличие выбранного плана
      if (!emailPattern.test(email)) {
        throw new Error('Неверный формат email');
      }
      if (!selectedPlan) {
        throw new Error('План не выбран');
      }

      const data = await payWithRegular(email, selectedPlan.days, selectedPlan.cost);

      if (!data?.paymentLink) {
        throw new Error('Не удалось получить платежную ссылку');
      }

      // Сначала обновляем состояние, потом закрываем модалку
      setPaymentLink(data.paymentLink);
      setShowModal(false);

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Произошла ошибка при оплате');
      setShowModal(false);
    } finally {
      setLoading(false);
    }
  };

  const themeClass = theme === 'dark' ? 'text-white bg-dark' : 'text-dark bg-light';
  const headerStyle = {
    borderRadius: '10px',
    padding: '20px',
    margin: '10px',
    backgroundColor: theme === 'dark' ? '#212529' : '#f8f9fa',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
  };

  return (
    <Container className={`pt-3 pb-5 ${themeClass} scrollable`}>
      <Row className="justify-content-md-center">
        <Col>
          <Card className={`d-flex align-items-center justify-content-center text-center ${themeClass} shadow-lg`} style={headerStyle}>
            <Card.Body>
              {paymentLink && selectedPlan ? (
                <>
                  <Card.Title>Оплата подписки на {selectedPlan.days} дней в Salew_Bot</Card.Title>
                  <Alert variant="success" className="mt-3 p-4">
                    <Card.Text>
                      Нажимая на кнопку <strong>Оплатить {selectedPlan.cost} рублей</strong>, вы соглашаетесь с&nbsp;
                      <a href="https://salew-bot.ru/agreement.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>
                        Пользовательским соглашением
                      </a>
                      &nbsp;и&nbsp;
                      <a href="https://salew-bot.ru/politika-obrabotki-personalnyh-dannyh.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: 'blue' }}>
                        Политикой обработки персональных данных
                      </a>.
                    </Card.Text>

                  </Alert>
                  <Button
                    variant="primary"
                    href={paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3"
                  >
                    Оплатить {selectedPlan.cost} рублей
                  </Button>
                </>

              ) : balance >= PRICE_PER_DAY_BONUS ? (
                <>
                  <Card.Title>Вы можете оплатить бонусами!</Card.Title>
                  <Card.Text>
                    Ваш реферальный баланс: {balance.toFixed(2)} руб.
                    <br />
                    Ваш ID: <Codee>{userInfo ? userInfo.user_id : null}</Codee>
                  </Card.Text>


                  <div className="d-flex flex-wrap justify-content-center">
                    {bonusDayOptions.map((d) => {
                      const cost = d * PRICE_PER_DAY_BONUS;
                      const can = balance >= cost;
                      return (
                        <Button
                          key={d}
                          variant={can ? "success" : "outline-secondary"}
                          className="m-2"
                          disabled={loading || !can}
                          onClick={() => handleBonusPayment(d)}
                        >
                          {d} дн • {cost} руб.
                        </Button>
                      );
                    })}
                  </div>
                  <hr />
                  <div className="d-flex flex-wrap justify-content-center">
                    {subscriptionPlans.map((plan) => (
                      <Button
                        key={plan.days}
                        variant={plan.cost === 50 ? "outline-warning" : "outline-primary"}
                        className="m-2"
                        size={plan.cost === 50 ? 'lg' : null}
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loading}
                      >
                        {plan.cost === 50 ? "Пробная подписка - " : null}{plan.days} дней - {plan.cost} руб.
                      </Button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <Card.Title>Выберите тариф</Card.Title>
                  <Card.Text>
                    Ваш ID: <Codee>{userInfo ? userInfo.user_id : null}</Codee>
                  </Card.Text>
                  <hr />
                  <div className="d-flex flex-wrap justify-content-center">
                    {subscriptionPlans.map((plan) => (
                      <Button
                        key={plan.days}
                        variant={plan.cost === 50 ? "outline-warning" : "outline-primary"}
                        className="m-2"
                        size={plan.cost === 50 ? 'lg' : null}
                        onClick={() => handlePlanSelect(plan)}
                        disabled={loading}
                      >
                        {plan.cost === 50 ? "Пробная подписка - " : null}{plan.days} дней - {plan.cost} руб.
                      </Button>
                    ))}
                  </div>
                </>
              )}
              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        setSelectedPlan('');
        setPaymentLink('');
      }}>
        <Modal.Header closeButton className={theme === 'dark' ? 'border-bottom border-secondary bg-dark text-white' : 'border-bottom border-secondary bg-light text-dark'}>
          <Modal.Title>Введите Email для оплаты</Modal.Title>
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'}>
          <Form>
            <Form.Group controlId="formEmail">
              <Form.Label>Email адрес</Form.Label>
              <Form.Control
                type="email"
                inputMode="email"
                placeholder="Введите email"
                className={`search-input ${theme === 'dark' ? 'bg-dark text-white' : 'bg-light text-dark'} ${emailError ? 'is-invalid' : ''}`}
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
              />
              {emailError && <div className="invalid-feedback">{emailError}</div>}
              {error && (
                <div className="mt-2 text-danger small">
                  ⚠️ {error}
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className={theme === 'dark' ? 'border-top border-secondary bg-dark text-white' : 'border-top border-secondary bg-light text-dark'}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleRegularPayment}
            disabled={loading || !email || emailError}
          >
            {loading ? 'Обработка...' : 'Оплатить'}
          </Button>
        </Modal.Footer>
      </Modal>
      <BottomNav />
    </Container>
  );
};

export default SubscriptionForm;
